import { supabase } from '../supabase';

export interface MotivationalMessage {
  message: string;
  author: string;
}

export interface InternalMessage {
  id: string;
  title: string;
  message: string;
  message_type: 'info' | 'announcement' | 'alert' | 'celebration';
  priority: number;
  created_at: string;
  expires_at: string | null;
  is_read?: boolean;
}

export class MessageRepository {
  /**
   * Get today's motivational message (deterministic based on date)
   */
  static async getDailyMotivationalMessage(): Promise<MotivationalMessage | null> {
    try {
      const { data, error } = await supabase
        .rpc('get_daily_motivational_message')
        .single();

      if (error) throw error;
      return data as MotivationalMessage;
    } catch (error) {
      console.error('Error fetching motivational message:', error);
      return {
        message: 'Make today amazing!',
        author: 'Captain Right Away'
      };
    }
  }

  /**
   * Get active internal messages for the current user
   */
  static async getInternalMessages(): Promise<InternalMessage[]> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return [];

      const { data, error } = await supabase
        .from('company_internal_messages')
        .select(`
          id,
          title,
          message,
          message_type,
          priority,
          created_at,
          expires_at,
          message_recipients!left (
            is_read
          )
        `)
        .eq('is_active', true)
        .eq('show_on_overview', true)
        .order('priority', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;

      return (data || []).map((msg: any) => ({
        id: msg.id,
        title: msg.title,
        message: msg.message,
        message_type: msg.message_type,
        priority: msg.priority,
        created_at: msg.created_at,
        expires_at: msg.expires_at,
        is_read: msg.message_recipients?.[0]?.is_read ?? false
      }));
    } catch (error) {
      console.error('Error fetching internal messages:', error);
      return [];
    }
  }

  /**
   * Mark a message as read
   */
  static async markMessageAsRead(messageId: string): Promise<void> {
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) return;

      await supabase
        .from('message_recipients')
        .update({ 
          is_read: true,
          read_at: new Date().toISOString()
        })
        .eq('message_id', messageId)
        .eq('user_id', user.user.id);
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  }
}
